import { useState, useEffect } from "react";
import StudentInfoDao from "../dao/StudentInfoDao";

/**
 * Hook to retrieve image URLs for a list of students
 * @param {Array} studentList - List of students (must include studentNumber)
 * @param {string|number} schoolYearKey - School year identifier
 * @param {string} token - Auth token
 * @param {Object} options - Additional options
 * @param {string} options.mimeType - Default MIME type (e.g., image/jpeg)
 * @param {string} options.fallbackImageUrl - Optional fallback for failed requests
 * @returns {{ images: Object, loading: boolean }}
 */
const useStudentImages = (
    studentList,
    schoolYearKey,
    token,
    { mimeType = "image/jpeg", fallbackImageUrl = null } = {}
) => {
    const [studentImages, setStudentImages] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!studentList?.length || !schoolYearKey || !token) return;

        const fetchImages = async () => {
            setLoading(true);

            const imageMap = {};

            await Promise.all(
                studentList.map(async ({ studentNumber }) => {
                    if (!studentNumber) return;

                    const options = {
                        action: "studentImageRead",
                        ignoreError: true,
                        schoolYearKey,
                        studentNumber,
                        token
                    };

                    try {
                        const response = await StudentInfoDao(options);
                        if (response?.data) {
                            const urlCreator = window.URL || window.webkitURL;
                            imageMap[studentNumber] = urlCreator.createObjectURL(
                                new Blob([response.data], { type: mimeType })
                            );
                        } else if (fallbackImageUrl) {
                            imageMap[studentNumber] = fallbackImageUrl;
                        }
                    } catch (err) {
                        if (fallbackImageUrl) {
                            imageMap[studentNumber] = fallbackImageUrl;
                        }
                        console.warn(`Image not found for student ${studentNumber}`);
                    }
                })
            );

            setStudentImages(imageMap);
            setLoading(false);
        };

        fetchImages();
    }, [studentList, schoolYearKey, token, mimeType, fallbackImageUrl]);

    return { images: studentImages, loading };
};

export default useStudentImages;
